import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { exportXlsxUrl, formatApiError } from "@/lib/api";
import { formatIdDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import SpbListRow from "@/components/spb/SpbListRow";
import { FileText, MagnifyingGlass, DownloadSimple, Plus, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function SPBList() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(() => {
    // Support ?q= param from Quick Actions FAB search
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  });
  const [status, setStatus] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("status") || "all";
  });
  const [loading, setLoading] = useState(true);
  const [batchDialog, setBatchDialog] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [sortMode, setSortMode] = useState("date_desc");
  const [pelangganFilter, setPelangganFilter] = useState("all");
  const [pelangganFacets, setPelangganFacets] = useState([]);
  const [pageSize, setPageSize] = useState(25); // 10 | 25 | 50
  const [pageIdx, setPageIdx] = useState(0); // 0-based
  // Sort keys: date_(desc|asc), no_spb_(asc|desc), distrik_(asc|desc), status_(asc|desc), plat_(asc|desc)

  const sortedItems = useMemo(() => {
    // Client-side pseudo-status: "auto_stale" further narrows already-fetched
    // dibatalkan rows to those flagged by the auto-cancel sweep.
    const base = status === "auto_stale"
      ? items.filter((s) => s.auto_cancel_stale === true)
      : items;
    const arr = [...base];
    const cmp = (a, b) => (a || "").localeCompare(b || "");
    if (sortMode === "date_desc") {
      arr.sort((a, b) => cmp(b.tanggal, a.tanggal) || cmp(b.no_spb, a.no_spb));
    } else if (sortMode === "date_asc") {
      arr.sort((a, b) => cmp(a.tanggal, b.tanggal) || cmp(a.no_spb, b.no_spb));
    } else if (sortMode === "no_spb_desc") {
      arr.sort((a, b) => cmp(b.no_spb, a.no_spb));
    } else if (sortMode === "no_spb_asc") {
      arr.sort((a, b) => cmp(a.no_spb, b.no_spb));
    } else if (sortMode === "distrik_asc") {
      arr.sort((a, b) => cmp(a.distrik_kode, b.distrik_kode) || cmp(a.distrik_nama, b.distrik_nama));
    } else if (sortMode === "distrik_desc") {
      arr.sort((a, b) => cmp(b.distrik_kode, a.distrik_kode) || cmp(b.distrik_nama, a.distrik_nama));
    } else if (sortMode === "status_asc") {
      arr.sort((a, b) => cmp(a.status, b.status) || cmp(b.tanggal, a.tanggal));
    } else if (sortMode === "status_desc") {
      arr.sort((a, b) => cmp(b.status, a.status) || cmp(b.tanggal, a.tanggal));
    } else if (sortMode === "plat_asc") {
      arr.sort((a, b) => cmp(a.no_polisi, b.no_polisi));
    } else if (sortMode === "plat_desc") {
      arr.sort((a, b) => cmp(b.no_polisi, a.no_polisi));
    } else if (sortMode === "pelanggan_asc") {
      arr.sort((a, b) => cmp(a.nama_pelanggan, b.nama_pelanggan));
    } else if (sortMode === "pelanggan_desc") {
      arr.sort((a, b) => cmp(b.nama_pelanggan, a.nama_pelanggan));
    }
    return arr;
  }, [items, sortMode, status]);

  const totalItems = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(pageIdx, totalPages - 1);
  const pagedItems = useMemo(
    () => sortedItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [sortedItems, currentPage, pageSize],
  );

  // Reset to page 1 whenever filters/sort change the underlying set OR page size changes.
  useEffect(() => { setPageIdx(0); }, [q, status, pelangganFilter, sortMode, pageSize]);

  const toggleSort = (col) => {
    setSortMode((m) => {
      if (col === "date") return m === "date_desc" ? "date_asc" : "date_desc";
      if (col === "no_spb") return m === "no_spb_asc" ? "no_spb_desc" : "no_spb_asc";
      if (col === "distrik") return m === "distrik_asc" ? "distrik_desc" : "distrik_asc";
      if (col === "status") return m === "status_asc" ? "status_desc" : "status_asc";
      if (col === "plat") return m === "plat_asc" ? "plat_desc" : "plat_asc";
      if (col === "pelanggan") return m === "pelanggan_asc" ? "pelanggan_desc" : "pelanggan_asc";
      return m;
    });
  };
  const arrow = (col) => {
    const map = {
      date: ["date_desc", "date_asc"],
      no_spb: ["no_spb_desc", "no_spb_asc"],
      distrik: ["distrik_desc", "distrik_asc"],
      status: ["status_desc", "status_asc"],
      plat: ["plat_desc", "plat_asc"],
      pelanggan: ["pelanggan_desc", "pelanggan_asc"],
    };
    const [desc, asc] = map[col] || [];
    if (sortMode === desc) return " ▼";
    if (sortMode === asc) return " ▲";
    return "";
  };

  const load = async (opts = {}) => {
    setLoading(true);
    const params = {};
    const searchTerm = opts.q !== undefined ? opts.q : q;
    if (searchTerm) params.q = searchTerm;
    // "auto_stale" is a client-side pseudo-status → fetch all cancelled from
    // backend, then further filter for auto_cancel_stale=true in `sortedItems`.
    if (status === "auto_stale") params.status = "dibatalkan";
    else if (status && status !== "all") params.status = status;
    if (pelangganFilter && pelangganFilter !== "all") params.q = pelangganFilter;
    try {
      const { data } = await api.get("/spb", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pelanggan facets once on mount — admin-only endpoint but SPBList
  // is also served to non-admins. Silent-fail keeps the page rendering.
  useEffect(() => {
    api.get("/spb/pelanggan-facets", { params: { limit: 200 } })
      .then((r) => setPelangganFacets(r.data || []))
      .catch(() => setPelangganFacets([]));
  }, []);

  // Refresh whenever the pelanggan facet filter changes
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [pelangganFilter]);

  // Refresh on status filter change
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status]);

  // Sync status filter with URL query param (react to same-route navigation, e.g. bell's "Lihat Semua")
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const next = params.get("status") || "all";
    setStatus((prev) => (prev === next ? prev : next));
  }, [location.search]);

  // Debounced search on q change — 400ms after user stops typing
  useEffect(() => {
    const t = setTimeout(() => { load({ q }); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Manajemen Surat</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <FileText size={22} weight="bold" />
            </span>
            Daftar SPB
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Semua Surat Pengambilan Barang.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setBatchResult(null); setBatchDialog(true); }}
                className="rounded-lg border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="batch-backfill-qr-button"
                title="Tambahkan QR PEMBUAT SPB otomatis ke semua SPB lama yang belum punya"
              >
                <ArrowsClockwise size={16} className="mr-2" />
                Backfill QR SPB Lama
              </Button>
              <a href={exportXlsxUrl()} target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-lg" data-testid="export-xlsx-button">
                  <DownloadSimple size={16} className="mr-2" />
                  Export Excel
                </Button>
              </a>
            </>
          )}
          {(user?.role === "admin" || user?.role === "admin_kantor") && (
            <Link to="/spb/new">
              <Button className="rounded-lg font-bold uppercase tracking-wider" data-testid="new-spb-button">
                <Plus size={16} className="mr-2" /> SPB Baru
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Cari no SPB, no polisi, supir, pelanggan..."
            className="rounded-lg pl-9 h-11"
            data-testid="spb-search-input"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="rounded-lg h-11 w-full md:w-56" data-testid="spb-status-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="belum_termuat">Belum Termuat</SelectItem>
            <SelectItem value="sudah_termuat">Sudah Termuat</SelectItem>
            <SelectItem value="mismatch">Mismatch</SelectItem>
            <SelectItem value="diverifikasi">Diverifikasi</SelectItem>
            <SelectItem value="dibatalkan">Dibatalkan</SelectItem>
            <SelectItem value="auto_stale" data-testid="filter-auto-stale">
              🕒 Otomatis Dibatalkan (Stale &gt;10 hari)
            </SelectItem>
          </SelectContent>
        </Select>
        {pelangganFacets.length > 0 && (
          <Select value={pelangganFilter} onValueChange={setPelangganFilter}>
            <SelectTrigger className="rounded-lg h-11 w-full md:w-72" data-testid="spb-pelanggan-filter">
              <SelectValue placeholder="Filter pelanggan" />
            </SelectTrigger>
            <SelectContent className="max-h-96">
              <SelectItem value="all">Semua Pelanggan ({pelangganFacets.reduce((s, f) => s + f.count, 0)})</SelectItem>
              {pelangganFacets.map((f) => (
                <SelectItem key={f.nama} value={f.nama} data-testid={`pelanggan-facet-${f.nama}`}>
                  {f.nama} ({f.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button onClick={load} className="rounded-lg h-11" data-testid="spb-search-button">
          <MagnifyingGlass size={16} className="mr-2" /> Cari
        </Button>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50">
            <tr className="text-left">
              <th
                className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
                onClick={() => toggleSort("no_spb")}
                data-testid="spb-sort-no-spb"
                title={sortMode === "no_spb_asc" ? "Klik: urutkan No SPB Z-A" : "Klik: urutkan No SPB A-Z"}
              >
                No SPB{arrow("no_spb")}
              </th>
              <th
                className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
                onClick={() => toggleSort("date")}
                data-testid="spb-sort-tanggal"
                title={sortMode === "date_desc" ? "Klik: urutkan tanggal terlama dulu" : "Klik: urutkan tanggal terbaru dulu"}
              >
                Tanggal{arrow("date")}
              </th>
              <th className="p-3 overline">Slot</th>
              <th
                className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
                onClick={() => toggleSort("distrik")}
                data-testid="spb-sort-distrik"
                title="Klik: urutkan Distrik A-Z / Z-A"
              >
                Distrik{arrow("distrik")}
              </th>
              <th
                className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
                onClick={() => toggleSort("pelanggan")}
                data-testid="spb-sort-pelanggan"
                title="Klik: urutkan Pelanggan A-Z / Z-A"
              >
                Pelanggan{arrow("pelanggan")}
              </th>
              <th className="p-3 overline">Produk</th>
              <th
                className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
                onClick={() => toggleSort("plat")}
                data-testid="spb-sort-plat"
                title="Klik: urutkan No Polisi A-Z / Z-A"
              >
                No Polisi{arrow("plat")}
              </th>
              <th className="p-3 overline">Supir</th>
              <th className="p-3 overline">Jumlah</th>
              <th className="p-3 overline">Termuat</th>
              <th
                className="p-3 overline cursor-pointer select-none hover:bg-secondary/80"
                onClick={() => toggleSort("status")}
                data-testid="spb-sort-status"
                title="Klik: urutkan Status A-Z / Z-A"
              >
                Status{arrow("status")}
              </th>
              <th className="p-3 overline">Sync GS</th>
              <th className="p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="p-8 text-center overline">Memuat...</td></tr>
            ) : sortedItems.length === 0 ? (
              <tr><td colSpan={13} className="p-8 text-center text-muted-foreground">Tidak ada data.</td></tr>
            ) : pagedItems.map((s) => (
              <SpbListRow key={s.id} s={s} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="spb-pagination">
        <div className="text-xs text-muted-foreground font-mono">
          {totalItems === 0 ? (
            "0 data"
          ) : (
            <>
              Menampilkan <strong>{currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalItems)}</strong>
              {" "}dari <strong>{totalItems}</strong> SPB
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="overline text-[10px] flex items-center gap-2">
            Per halaman
            <select
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              className="border border-border bg-background rounded-lg h-8 px-2 text-sm font-mono"
              data-testid="spb-page-size"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </label>
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg"
            onClick={() => setPageIdx((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            data-testid="spb-page-prev"
          >
            ← Sebelumnya
          </Button>
          <span className="text-xs font-mono tabular-nums" data-testid="spb-page-info">
            {currentPage + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg"
            onClick={() => setPageIdx((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            data-testid="spb-page-next"
          >
            Berikutnya →
          </Button>
        </div>
      </div>

      <AlertDialog open={batchDialog} onOpenChange={setBatchDialog}>
        <AlertDialogContent className="rounded-lg" data-testid="batch-backfill-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase tracking-wider text-sm">
              Backfill QR PEMBUAT untuk SPB Lama
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-xs">
              <span className="block">
                Aksi ini scan <strong>semua SPB</strong> dan menambahkan tanda tangan
                QR "PEMBUAT SPB" (pakai akun kamu sebagai signer) untuk setiap SPB
                yang belum punya.
              </span>
              <span className="block">
                SPB yang sudah punya QR PEMBUAT <strong>di-skip</strong> — aman
                dijalankan berkali-kali. Proses idempotent.
              </span>
              <span className="block text-amber-700 font-bold">
                Estimasi ~2 detik per 100 SPB. Jangan close tab sampai selesai.
              </span>
              {batchResult && (
                <span
                  className="block bg-emerald-50 border border-emerald-400 p-2 mt-2 font-mono text-[11px]"
                  data-testid="batch-backfill-result"
                >
                  ✓ Selesai: <strong>{batchResult.backfilled}</strong> SPB
                  di-backfill, <strong>{batchResult.skipped}</strong> di-skip
                  (sudah punya), total <strong>{batchResult.scanned}</strong> di-scan.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-lg"
              disabled={batchBusy}
              data-testid="batch-backfill-cancel"
            >
              {batchResult ? "Tutup" : "Batal"}
            </AlertDialogCancel>
            {!batchResult && (
              <AlertDialogAction
                className="rounded-lg bg-emerald-700 hover:bg-emerald-800"
                disabled={batchBusy}
                onClick={async (e) => {
                  e.preventDefault();
                  setBatchBusy(true);
                  try {
                    const { data } = await api.post("/spb/qr-batch-backfill-pembuat");
                    setBatchResult(data);
                    toast.success(
                      `Backfill selesai — ${data.backfilled} SPB dapat QR baru`,
                    );
                  } catch (err) {
                    toast.error(formatApiError(err));
                  } finally {
                    setBatchBusy(false);
                  }
                }}
                data-testid="batch-backfill-confirm"
              >
                {batchBusy ? (
                  <>
                    <ArrowsClockwise size={14} className="mr-2 animate-spin" />
                    Sedang backfill…
                  </>
                ) : (
                  <>Jalankan Backfill</>
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
